import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LivenessCapture from '@/components/LivenessCapture';
import FloatingDock from '@/components/layout/FloatingDock';
import { ArrowLeft, Shield, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

const LivenessVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromRegistration = location.state?.fromRegistration;
  const nextStep = location.state?.nextStep || '/dashboard';

  const handleComplete = (result) => {
    if (result.is_verified) {
      toast.success('Identity verified! Your account is now fully activated.');
      // If from registration, go to onboarding, otherwise go to settings
      if (fromRegistration) {
        navigate(nextStep, { state: location.state });
      } else {
        navigate('/settings');
      }
    } else {
      toast.error('Verification unsuccessful. Please try again.');
    }
  };

  const handleCancel = () => {
    if (fromRegistration) {
      toast.warning('Identity verification is required to use Socyy. You can complete it later from Settings.');
      navigate('/dashboard');
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pb-32" data-testid="liveness-verification-page">
      <div className="max-w-4xl mx-auto px-6 pt-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          {!fromRegistration && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
          )}
          <div>
            <h1 className="text-3xl font-bold text-white">
              {fromRegistration ? 'Verify Your Identity' : 'Identity Verification'}
            </h1>
            <p className="text-gray-400">
              {fromRegistration 
                ? 'Complete this quick check to activate your account'
                : 'Verify you\'re a real person with a quick selfie check'
              }
            </p>
          </div>
        </div>

        {/* Registration Warning Banner */}
        {fromRegistration && (
          <Card className="p-4 rounded-2xl border border-yellow-500/50 bg-yellow-900/20 mb-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0" />
              <div>
                <p className="text-yellow-200 font-semibold">Required for New Accounts</p>
                <p className="text-yellow-300/80 text-sm">
                  This verification helps protect our community from bots and fake accounts. 
                  It only takes about 30 seconds.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Anti-Spoofing Notice */}
        <Card className="p-4 rounded-2xl border border-red-500/30 bg-red-900/10 mb-6">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-red-400 flex-shrink-0" />
            <div>
              <p className="text-red-200 font-semibold">Anti-Spoofing Protection Active</p>
              <p className="text-red-300/80 text-sm">
                Our AI detects photos, screens, and masks. Only real, live faces are accepted.
                Please ensure good lighting and don't hold up any images or devices.
              </p>
            </div>
          </div>
        </Card>

        {/* Liveness Capture Component */}
        <LivenessCapture 
          onComplete={handleComplete}
          onCancel={handleCancel}
        />

        {/* Info Section */}
        <div className="mt-8 p-6 bg-slate-800/50 rounded-2xl border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4">How it works</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-300">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold flex-shrink-0">1</div>
              <div>
                <p className="font-medium text-white">Start Verification</p>
                <p>Allow camera access and position your face in the oval guide</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold flex-shrink-0">2</div>
              <div>
                <p className="font-medium text-white">Follow Prompts</p>
                <p>Complete 3-5 random actions like smiling, turning your head, or blinking</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold flex-shrink-0">3</div>
              <div>
                <p className="font-medium text-white">Get Verified</p>
                <p>Pass 80% of challenges to verify your identity and unlock features</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="mt-6 p-4 bg-yellow-900/30 rounded-xl border border-yellow-700/50">
          <p className="text-yellow-300 text-sm">
            <strong>Tips for success:</strong> Use good lighting, keep your face centered, move slowly when performing actions, and ensure a plain background behind you. Do NOT use photos, screens, or masks.
          </p>
        </div>
      </div>

      {!fromRegistration && <FloatingDock />}
    </div>
  );
};

export default LivenessVerification;
